<template>
    <div class="videoMessage" @mouseenter="videoHovered = true" @mouseleave="videoHovered = false">
        <custom-video-player
            class="video-player-box"
            ref="videoPlayer"
            :src="videoSrc()"
            :max-width="400"
            :max-height="300"
            :playback-rates="[0.7, 1.0, 1.5, 2.0]"
            :muted="false"
            :timestamp="displayTimestamp"
            :timestamp-tooltip="timestampTooltip"
        />
        <div class="actor-info-bar" v-if="actorInfo">
            <i class="material-icons">info_outline</i>
            <span v-if="!actorInfo.link">{{ actorInfo.text }}</span>
            <router-link v-else :to="actorInfo.link" class="actor-link">
                {{ actorInfo.text }}
                <i class="material-icons" style="font-size: 10px;">open_in_new</i>
            </router-link>
        </div>
        <div v-if="videoHovered && message" class="video-gallery-btn" @click.stop="$emit('open-gallery', message)">
            <span class="material-icons">collections</span>
        </div>
    </div>
</template>

<script>
import CustomVideoPlayer from "@/components/common/CustomVideoPlayer.vue";
import moment from "moment";
import _ from "lodash";
import { mapState } from "vuex";

export default {
    name: "VideoMessage",
    components: {
        CustomVideoPlayer,
    },
    props: {
        data: {
            type: Object,
            required: true,
        },
        message: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            videoHovered: false,
        };
    },
    computed: {
        ...mapState({
            users: function (state) { return _.get(state, "user.users.data", []); },
        }),
        actorInfo: function () {
            if (!this.message) return null;
            const data = this.message.raw || {};
            const actorDetail = data.actorDetail || {};

            if (actorDetail.type === "automation" || data.fromAutomation) {
                const automationId = actorDetail.automation || data.automationId;
                const blockId = actorDetail.blockId;
                const automationName = actorDetail.automationName;
                const text = automationName
                    ? this.$t("message.sentByAutomation", { name: automationName })
                    : this.$t("message.sentByAutomationGeneric");
                let link = null;
                if (automationId) {
                    link = "/automations/" + automationId + "/design" + (blockId ? "/block/" + blockId : "");
                }
                return { text: text, link: link };
            }

            if (actorDetail.type === "chatbot") {
                const storyId = actorDetail.chatbotStory;
                const version = actorDetail.chatbotStoryVersion;
                const blockId = actorDetail.blockId;
                const isAIAssistant = blockId && (blockId === "ai_assistant" || blockId === "AI_ASSISTANT");
                const storyName = actorDetail.chatbotStoryName;
                const text = storyName
                    ? this.$t("message.sentByChatbot", { name: storyName })
                    : this.$t("message.sentByChatbotGeneric");
                let link = null;
                if (storyId && version) {
                    link = "/chatbot/story/" + storyId + "/" + version + (blockId && !isAIAssistant ? "/block/" + blockId : "");
                }
                return { text: text, link: link };
            }

            let text = null;
            if (data.fromBroadcast || actorDetail.type === "broadcast") text = this.$t("message.sentByBroadcast");
            else if (data.fromAPI || actorDetail.type === "api") text = this.$t("message.sentByAPI");
            else {
                let username = data.username || "";
                if (!username && data.user) {
                    const userHashMap = _.keyBy(this.users, "_id");
                    if (typeof data.user === "object") {
                        username = _.get(data.user, "name", "");
                    } else {
                        username = _.get(userHashMap, "[" + data.user + "].name", "");
                    }
                }
                if (username) text = this.$t("message.sentByUser", { name: username });
            }
            return text ? { text: text, link: null } : null;
        },
        displayTimestamp() {
            return this.formattedTimestamp();
        },
        timestampTooltip() {
            const timestamp =
                this.message?.raw?.createdDate ||
                this.message?.timestamp ||
                this.message?.createdAt ||
                this.message?.time ||
                this.data?.createdDate ||
                this.data?.createdAt ||
                this.data?.timestamp ||
                this.data?.time;
            if (!timestamp) return "";
            return moment(timestamp).format("DD.MM.YYYY HH:mm");
        },
    },
    methods: {
        videoSrc() {
            const asset = this.data.asset;
            const path = asset?.path || "";
            if (path === "") return null;
            const isWhatsapp = path.indexOf("whatsapp") !== -1;
            const isInstagram = path.indexOf("instagram") !== -1;
            const isMessenger = path.indexOf("messenger") !== -1;
            const isTelegram = path.indexOf("telegram") !== -1;
            const isFacebook = path.indexOf("facebook") !== -1;
            const isTable = path.indexOf("tables/attachments") !== -1;
            const isUnipile = path.indexOf("unipile") !== -1;
            const isChat = path.indexOf("chat") !== -1;
            const isApiMessages = path.indexOf("api-messages") !== -1;
            const src =
                isWhatsapp || isInstagram || isTelegram || isMessenger || isFacebook || isTable || isUnipile || isChat || isApiMessages
                    ? `https://static.supsis.live/${asset.path}`
                    : asset.v2Format && asset.cdnUrl
                    ? asset.cdnUrl
                    : `https://static.supsis.live/${asset.path + asset._id + (asset.ext ? "." + asset.ext : "")}`;
            return src;
        },
        formattedTimestamp() {
            const timestamp =
                this.message?.raw?.createdDate ||
                this.message?.timestamp ||
                this.message?.createdAt ||
                this.message?.time ||
                this.data?.createdDate ||
                this.data?.createdAt ||
                this.data?.timestamp ||
                this.data?.time;
            if (!timestamp) return "";
            return moment(timestamp).format("HH:mm");
        },
    },
};
</script>

<style lang="scss" scoped>
.videoMessage {
    display: inline-block;
    position: relative;

    .video-player-box {
        max-width: 400px;
        max-height: 300px;
    }

    .actor-info-bar {
        display: flex;
        align-items: center;
        gap: 3px;
        padding: 4px 4px 0;
        color: #8696a0;
        font-size: 10px;
        line-height: 1.2;

        .material-icons {
            font-size: 12px;
        }

        .actor-link {
            color: #00a5f4;
            text-decoration: underline;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 2px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;

            &:hover {
                opacity: 0.8;
            }
        }
    }

    .video-gallery-btn {
        position: absolute;
        top: 8px;
        right: 8px;
        background: rgba(0, 0, 0, 0.55);
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.2s;

        .material-icons {
            color: #fff;
            font-size: 18px;
        }

        &:hover {
            background: rgba(0, 0, 0, 0.8);
        }
    }
}
</style>
